import * as React from "react";
import {Accordion, AccordionSection, Table} from "@vebox/etui-core";
import {EditableSetting, UpdatingSettings} from "../../data/models/UpdatingSetting";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {STATUS_TEXTS, StatusChip} from "../../components/StatusChip";
import {accordionStyle} from "../../ReadAndWrite";
import {
    containsAllDoneSettings,
    containsUpdatingSettings,
    createColumnsForUpdatingTable,
    mapSettingsToTableData
} from "./utils";
import {filterTableData, groupEditableSettingsByGroupName} from "../../ReadAndWrite/utils";
import {Theme} from "@vebox/etui-themes";
import {useTheme} from "@vebox/etui-system";
import {GroupedMappedUpdatingSettings} from "./types";
import {ReadAndWriteContext} from "../../../providers/ReadAndWriteProvider";

interface UpdatingConfigurationTableData {
    settings: EditableSetting[],
    updatingSettings: UpdatingSettings
    isValidating: boolean
}


export const UpdatingConfigurationTable: React.FC<UpdatingConfigurationTableData> = ({
                                                                                         settings,
                                                                                         updatingSettings,
                                                                                         isValidating
                                                                                     }) => {
    const {getTableHeadersName, registerFilterText, AppTranslations} = React.useContext(ReadAndWriteContext);
    const theme = useTheme();
    const groupedSettings = React.useMemo(() => {
        return groupEditableSettingsByGroupName(settings?.filter(filterTableData(registerFilterText)))
    }, [settings]);

    const dataSource: GroupedMappedUpdatingSettings = React.useMemo(() => {
        const result: GroupedMappedUpdatingSettings = {};
        groupedSettings.forEach((group) => {
            result[group.groupName] = mapSettingsToTableData(group.registers, updatingSettings?.updatingSettings,
                isValidating);
        });
        return result;
    }, [groupedSettings, updatingSettings?.valuesUpdated, isValidating]);

    const renderHeaderFarItem = (registers: EditableSetting[], isUpdating: boolean, name: string) => () => {
        const isAllDone = containsAllDoneSettings(registers, updatingSettings?.updatingSettings, isValidating, name);
        if (isAllDone) {
            return <StatusChip status={STATUS_TEXTS.COMPLETED}/>
        } else if (isUpdating) {
            return <StatusChip status={STATUS_TEXTS.IN_PROGRESS}/>
        } else {
            return null;
        }
    }

    return (
        <VeBoxCard className={"Section rw-card"}>
            <VeBoxCard.Header
                className={"rw-card-header"}
                title={AppTranslations.readAndWrite.parameters}
                singleLineTitle={true}
            />
            <VeBoxCard.Body className={"rw-card-body"}>
                <Accordion>
                    {groupedSettings.map((accordionSection, index) => {
                            const isUpdating = containsUpdatingSettings(accordionSection.registers,
                                updatingSettings?.updatingSettings,
                                isValidating);
                            return <AccordionSection
                                key={index}
                                name={accordionSection.groupName}
                                defaultOpen={isUpdating}
                                keepSectionContentInDOM={true}
                                styles={{
                                    ...accordionStyle(theme as Theme),
                                    sectionContent: {height: ((accordionSection.registers.length + 1) * 39) + "px"}
                                }}
                                renderHeaderFarItem={renderHeaderFarItem(accordionSection.registers, isUpdating,
                                    accordionSection.groupName)}
                            >
                                <Table
                                    pagination={false}
                                    dataSource={dataSource[accordionSection.groupName]}
                                    columns={createColumnsForUpdatingTable(getTableHeadersName)}
                                />
                            </AccordionSection>
                        }
                    )}
                </Accordion>
            </VeBoxCard.Body>
        </VeBoxCard>
    );
};
